// assets/edm-bundle-desktop-manager.js
// Desktop Manager - Handles desktop card/slot clicks and animations
// Context7: Manager Pattern - Separates desktop concerns from Controller
// AUDIT FIX: Implements AbortController pattern for event listeners

window.EdumonBundle = window.EdumonBundle || {};

window.EdumonBundle.DesktopManager = {
  /**
   * Initialize desktop manager
   * @param {Object} config - Configuration
   * @param {Element} config.el - Bundle builder element
   * @param {Object} config.state - State instance (by reference)
   * @param {Array} config.$slots - Slot elements
   * @param {Array} config.$cards - Card elements
   * @param {number} config.maxSlots - Maximum slots
   * @param {Map} config.cardImgByVid - Card image map (variantId -> img element)
   * @param {Function} config.updateUI - Update UI function
   * @param {Function} config.markDirty - Mark dirty function
   * @returns {Object} Manager instance
   */
  init: function(config) {
    const Config = window.EdumonBundle.Config;
    const Utils = window.EdumonBundle.Utils;
    const UIRenderDesktop = window.EdumonBundle.UIRenderDesktop;
    
    const {
      el,
      state,
      $slots,
      $cards,
      maxSlots,
      cardImgByVid,
      updateUI,
      markDirty
    } = config;
    
    // AUDIT FIX: Create AbortController for all event listeners
    const abortController = new AbortController();
    const signal = abortController.signal;
    
    // Animation (Desktop)
    const flyToSlot = async (fromImg, toSlot) => {
      if (!fromImg || !toSlot) { 
        if (updateUI) updateUI(); 
        return; 
      }
      void fromImg.offsetWidth; 
      void toSlot.offsetWidth;
      const imgRect = fromImg.getBoundingClientRect();
      const slotRect = toSlot.getBoundingClientRect();
      if (slotRect.width === 0 || slotRect.height === 0) { 
        if (updateUI) updateUI(); 
        return; 
      }
      const ghost = fromImg.cloneNode(true);
      Object.assign(ghost.style, {
        position: 'fixed',
        left: `${imgRect.left}px`,
        top: `${imgRect.top}px`,
        width: `${imgRect.width}px`,
        height: `${imgRect.height}px`,
        zIndex: '9999',
        pointerEvents: 'none',
        willChange: 'transform, opacity'
      });
      document.body.appendChild(ghost);
      const scaleX = slotRect.width / imgRect.width;
      const scaleY = slotRect.height / imgRect.height;
      const dx = slotRect.left - imgRect.left;
      const dy = slotRect.top - imgRect.top;
      try {
        const anim = ghost.animate(
          [{ transform: 'translate(0,0) scale(1)', opacity: 1 },
           { transform: `translate(${dx}px, ${dy}px) scale(${scaleX}, ${scaleY})`, opacity: 1 }],
          { duration: Config.CONSTANTS.ANIMATION_DURATION_MS, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' }
        );
        await anim.finished;
      } catch(e) {} finally {
        ghost.style.willChange = 'auto';
        ghost.remove();
      }
      if (updateUI) updateUI();
    };
    
    const firstEmptySlot = () => $slots.find(s => !s.classList.contains(Config.CLASSES.FILLED));
    
    // Card-Clicks (Desktop add)
    // AUDIT FIX: Use AbortController for card click listeners
    // DESKTOP FIX: closest() braucht einen gültigen CSS-Selector mit Punkt
    el.querySelectorAll(Config.SELECTORS.CARD_MEDIA).forEach(cardMedia => {
      const card = cardMedia.closest(Config.SELECTORS.CARDS);
      const vid = card?.dataset.variantId;
      const img = cardMedia.querySelector('img');
      cardMedia.addEventListener('click', async () => {
        if (!vid) {
          console.warn('[Bundle Builder] Desktop: No variant ID found for card', card);
          return;
        }
        if (Object.values(state.items).reduce((a,b)=>a+b,0) >= maxSlots) return;
        const targetSlot = firstEmptySlot();
        if (!targetSlot) {
          console.warn('[Bundle Builder] Desktop: No empty slot found');
          return;
        }
        state.addItem(vid, 1);
        if (markDirty) markDirty();
        if (img) await flyToSlot(img, targetSlot);
        else if (updateUI) updateUI();
      }, { signal });
    });
    
    // Slot-Clicks (Desktop remove)
    // AUDIT FIX: Use AbortController for slot click listeners
    $slots.forEach(slot => {
      slot.addEventListener('click', () => {
        // Desktop-only: Mobile wird in MobileManager behandelt
        if (Utils.isMobile()) return;
        const vid = slot.dataset.variantId;
        if (!vid) return;
        const cur = state.items[vid] || 0;
        if (cur <= 1) {
          state.removeItem(vid);
        } else {
          state.items[vid] = cur - 1;
          state.markDirty();
        }
        const totalQty = Object.values(state.items).reduce((a,b)=>a+b,0);
        if (totalQty > maxSlots) {
          console.warn('[Bundle Builder] Slot click: Total quantity exceeds maximum, capping');
          state.setItems(Utils.capToMaximum(state.items, maxSlots));
        }
        if (markDirty) markDirty();
        if (updateUI) updateUI();
      }, { signal });
    });
    
    // Return manager instance
    return {
      // Public API
      flyToSlot,
      firstEmptySlot,
      
      // AUDIT FIX: Cleanup method
      destroy: function() {
        // Abort all event listeners
        abortController.abort();
      }
    };
  }
};
